import { Component, OnInit, Input, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { IMap } from 'src/app/interfaces/map';
import { IFlight } from 'src/app/interfaces/flight';
import { JourneyService } from 'src/app/services/journey.service';
import { IRoute } from 'src/app/interfaces/route';
import { TranslateService } from 'src/app/services/translate.service';
import { UtilsService } from 'src/app/services/utils.service';

@Component({
  selector: 'app-journey-weeks',
  templateUrl: './journey-weeks.component.html',
  styleUrls: ['./journey-weeks.component.scss']
})
export class JourneyWeeksComponent implements OnInit {
  @Input() map: IMap;
  @Input() routes: { [route: string]: IRoute };
  @Input() mapReady: EventEmitter<boolean>;
  public totalCo2: number;
  public totalBreaths: string;
  public totalTrees: number;
  public totalFlight: string;
  public totalNox: number;
  public totalNoxYear: number;
  public co2Message: number;
  public journeyMessage: string;
  public noxMessage: string;
  public healthMessage: string;
  public emissionMessage: string;
  public share = {
    description: '',
    url: window.location.href
  };
  private flights: IFlight[] = [];
  private messages = {
    DRIVING: this.translate.get('MESSAGE_DRIVING'),
    BICYCLING: this.translate.get('MESSAGE_BICYCLING'),
    WALKING: this.translate.get('MESSAGE_WALKING'),
    TRANSIT: this.translate.get('MESSAGE_TRANSIT'),
  };
  private defaultShareMessage = this.translate.get('SOCIALMEDIA_SHARE');
  private journeyShareMessages = {
    DRIVING: this.translate.get('SOCIALMEDIA_DRIVING'),
    BICYCLING: this.translate.get('SOCIALMEDIA_BICYCLING'),
    WALKING: this.translate.get('SOCIALMEDIA_WALKING'),
    TRANSIT: this.translate.get('SOCIALMEDIA_TRANSIT'),
  };

  constructor(
    private journeyService: JourneyService,
    private translate: TranslateService,
    private utils: UtilsService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  ngOnInit() {
    this.journeyService.flights.subscribe((flights: IFlight[]) => this.flights = flights);
    this.mapReady.subscribe(isReady => {
      if (isReady && this.map) {
        this.setTotal();
      }
    });
    this.journeyService.sliderChange.subscribe(() => {
      if (this.map) {
        this.setTotal();
      }
    });
  }

  get timesPerWeek() {
    return this.journeyService.timesPerWeek;
  }

  set timesPerWeek(timesPerWeek: number) {
    this.journeyService.timesPerWeek = timesPerWeek;
  }

  onSliderChange(event) {
    this.timesPerWeek = event.value;
    this.journeyService.sliderChange.emit();
    if (this.map) {
      this.setTotal();
    }
  }

  setTotal() {
    const weeksYear = 52.1429;
    this.totalNoxYear = 0;

    this.totalCo2 = Math.round(this.timesPerWeek * weeksYear * this.routes[this.map.mode].co2Kg);
    const lessThanCarCo2 = Math.round(this.timesPerWeek * weeksYear * this.routes.DRIVING.co2Kg) - this.totalCo2;

    this.totalTrees = Math.round(this.totalCo2 / 5.9);
    this.totalBreaths = this.utils.formatNumber(Math.round(this.timesPerWeek * this.routes[this.map.mode].breaths));
    this.totalNox = Number((this.timesPerWeek * this.routes[this.map.mode].noxG).toFixed(1));

    const flight = this.flights.filter(f => f.distance * 0.16 < this.totalCo2)[0];
    this.totalFlight = flight ? flight.to : null;
    this.journeyMessage = this.messages[this.map.mode];
    this.share.description = this.defaultShareMessage.replace('$$SHARE_MESSAGE', this.journeyShareMessages[this.map.mode]);
    this.noxMessage = this.translate.get('NOX_MESSAGE_ACTIVE');

    const totalEmission = Math.round(90 / (this.timesPerWeek * this.routes[this.map.mode].co2Kg));
    const totalMeltedIce = (this.totalCo2 / 1000) * 3;
    let co2string = 'CO2_MESSAGE_ACTIVE';

    if (totalEmission > 40) {
      this.emissionMessage = null;
    } else {
      this.emissionMessage = this.translate.get('TOTAL_EMISSION_MESSAGE')
        .replace('$$TOTAL_EMISSION_WEEKS_MESSAGE',
          this.translate.get(totalEmission < 0 ? 'LESS_THAN_A_WEEK' :
            totalEmission > 40 ? 'MORE_THAN_X_WEEKS' : 'WEEKS_EMISSON'))
        .replace('$TOTAL_EMISSION_WEEKS', this.utils.formatNumber(totalEmission));
    }

    if (this.map.mode === 'DRIVING') {
      co2string = 'CO2_MESSAGE_CARS';
      this.totalNoxYear = this.totalNox * weeksYear;

      this.noxMessage = this.translate.get('NOX_MESSAGE_CAR')
        .replace('$$NOX_LIVES_CAR', this.utils.formatNumber(Math.round((
          this.routes[this.map.mode].durationSecs / 60) * 16.74 * this.timesPerWeek)))
        .replace('$$NOX_TOTAL_CAR', this.utils.formatNumber(Number(this.totalNoxYear.toFixed(1))))
        .replace('$$NOX_TOTAL_TIME', this.formatTimeAmount(this.routes[this.map.mode].durationSecs * this.timesPerWeek));

      this.healthMessage = this.translate.get('HEALTH_MESSAGE_CAR');
    }

    if (this.map.mode === 'TRANSIT') {
      co2string = 'CO2_MESSAGE_TRANSIT';
      this.totalNoxYear = this.totalNox * weeksYear;

      const lessNoxThanCar = (Number((this.timesPerWeek * this.routes.DRIVING.noxG).toFixed(1)) * weeksYear) - this.totalNoxYear;

      this.noxMessage = this.translate.get('NOX_MESSAGE_BUS')
        .replace('$$NOX_LESS_BUS', this.utils.formatNumber(Number(lessNoxThanCar.toFixed(1))))
        .replace('$$NOX_TOTAL_BUS', this.utils.formatNumber(Number(this.totalNoxYear.toFixed(1))));

      this.healthMessage = this.translate.get('HEALTH_MESSAGE_TRANSIT');
    }


    if (this.map.mode === 'BICYCLING' || this.map.mode === 'WALKING') {
      const activityGoal = 150;
      const durationM = (this.routes[this.map.mode].durationSecs / 60) * this.timesPerWeek;
      this.healthMessage = this.translate
                            .get(durationM < activityGoal ? 'HEALTH_MESSAGE_BELOW_AG' : 'HEALTH_MESSAGE_ABOVE_AG')
                            .replace('$$ACTIVITY_PERCENTAGE', Math.round((durationM * 100) / activityGoal));
    }

    this.co2Message = this.translate.get(co2string)
                        .replace('$$TOTAL_CO2_KG', this.utils.formatNumber(this.totalCo2))
                        .replace('$$CO2_LESS_THAN_CAR', this.utils.formatNumber(Number(lessThanCarCo2.toFixed(1))))
                        .replace('$$ARTIC_SQM', this.utils.formatNumber(Number(totalMeltedIce.toFixed(1))));
  }

  formatTimeAmount(seconds: number) {
    let amountTime = '';

    const days = Math.floor(seconds / 86400);
    seconds %= 86400;

    const hours = Math.floor(seconds / 3600);
    seconds %= 3600;

    const minutes = Math.round(seconds / 60);
    seconds %= 60;

    if (days > 0) { amountTime += `${days} ${this.translate.get(days === 1 ? 'DAY' : 'DAYS')} `; }

    if (hours > 0) { amountTime += `${hours} ${this.translate.get(hours === 1 ? 'HOUR' : 'HOURS')} `; }

    if (minutes > 0) { amountTime += `${minutes} ${this.translate.get(minutes === 1 ? 'MINUTE' : 'MINUTES')} `; }

    if (amountTime === '') { amountTime = '1 ' + this.translate.get('MINUTE'); }

    return amountTime;
  }

  scrollToTop() {
    window.scroll(0, 0);
  }

  detectChanges() {
    this.changeDetector.detach();
    this.changeDetector.detectChanges();
    this.changeDetector.reattach();
  }

}


